import { cloneDeep, countBy, every, isNil, some } from "lodash-es";
import moment from "moment-timezone";
import YAML from "yaml";

import { PplCriteria, PplRule } from "../store/pomeriumApi";

export class Document {
  rules: Rules;

  constructor() {
    this.rules = new Rules();
  }

  static fromJSON(source: string): Document {
    const value = JSON.parse(source);
    return this.fromValue(value);
  }

  static fromValue(value: unknown): Document {
    const doc = new Document();

    if (Array.isArray(value)) {
      value.forEach((obj) => {
        Rules.fromValue(obj).forEach((rule) => {
          doc.rules.push(rule);
        });
      });
    } else if (typeof value === "object") {
      Rules.fromValue(value).forEach((rule) => {
        doc.rules.push(rule);
      });
    }

    return doc;
  }

  static fromYAML(source: string): Document {
    const value = YAML.parse(source);
    return this.fromValue(value);
  }

  containsCriterion(action: Action, criterionName: string): boolean {
    return some(this.rules, (rule) => rule.containsCriterion(criterionName));
  }

  removeCriterion(action: Action, criterionName: string): void {
    for (let i = this.rules.length - 1; i >= 0; i--) {
      if (this.rules[i].action === action) {
        this.rules[i].removeCriterion(criterionName);
      }
      if (this.rules[i].length === 0) {
        this.rules.splice(i, 1);
      }
    }
  }

  toJSON(): string {
    return JSON.stringify(this.toValue());
  }

  toValue(): any {
    return this.rules.toValue();
  }

  toYAML(): string {
    if (this.rules.length === 0) {
      return "";
    }
    return YAML.stringify(this.toValue());
  }
}

export class Rules extends Array<Rule> {
  static fromValue(value: unknown): Rules {
    if (Array.isArray(value)) {
      return new Rules();
    }

    if (typeof value !== "object" || value === null || value === undefined) {
      return new Rules();
    }

    const rules = new Rules();
    ALL_ACTIONS.forEach((a) => {
      if (a in value) {
        const rule = Rule.fromValue(a, (value as any)[a]);
        if (rule) {
          rules.push(rule);
        }
      }
    });
    return rules;
  }

  toValue(): any {
    const useObject = every(
      countBy(this, (r) => r.action),
      (cnt) => cnt <= 1,
    );
    if (useObject) {
      return this.reduce((obj, rule) => {
        Object.entries(rule.toValue()).map(([k, v]) => {
          (obj as any)[k] = v;
        });
        return obj;
      }, {});
    }
    return this.map((rule) => rule.toValue());
  }
}

export class Rule {
  action: Action;
  and: Criteria | null;
  or: Criteria | null;
  not: Criteria | null;
  nor: Criteria | null;

  constructor(
    action: Action,
    body?: { [op in LogicalOperator]?: Iterable<Record<string, unknown>> },
  ) {
    this.action = action;
    this.and = Criteria.fromValue(body?.and);
    this.or = Criteria.fromValue(body?.or);
    this.not = Criteria.fromValue(body?.not);
    this.nor = Criteria.fromValue(body?.nor);
  }

  static fromValue(action: Action, value: unknown): Rule | null {
    if (Array.isArray(value)) {
      return null;
    }

    if (typeof value !== "object" || value === null || value === undefined) {
      return null;
    }

    const rule = new Rule(action);
    ALL_LOGICAL_OPERATORS.forEach((op) => {
      if (op in value) {
        rule[op] = Criteria.fromValue((value as any)[op]);
      }
    });
    return rule;
  }

  get length(): number {
    return ALL_LOGICAL_OPERATORS.reduce((cnt, op) => {
      return cnt + (this[op]?.length || 0);
    }, 0);
  }

  containsCriterion(criterionName: string): boolean {
    return some(ALL_LOGICAL_OPERATORS, (op) =>
      some(this[op] || [], (criterion) => criterion.name === criterionName),
    );
  }

  removeCriterion(criterionName: string): void {
    ALL_LOGICAL_OPERATORS.forEach((op) => {
      for (let i = (this[op]?.length || 0) - 1; i >= 0; i--) {
        if (this[op]?.[i].name === criterionName) {
          this[op].splice(i, 1);
        }
      }
    });
  }

  toValue(): any {
    const body = {};
    ALL_LOGICAL_OPERATORS.forEach((op) => {
      if ((this[op]?.length || 0) > 0) {
        (body as any)[op as string] = this?.[op]?.toValue();
      }
    });
    return { [this.action as string]: body };
  }
}

export class Criteria extends Array<Criterion> {
  static fromValue(value: unknown): Criteria | null {
    if (!Array.isArray(value)) {
      return null;
    }

    const criteria = new Criteria();
    value.forEach((v) => {
      const criterion = Criterion.fromValue(v);
      if (criterion) {
        criteria.push(criterion);
      }
    });
    return criteria;
  }

  toValue(): any {
    return this.map((v) => v.toValue());
  }
}

export class Criterion {
  name: string;
  path: string | null;
  data: any;

  constructor(name: string, path: string | null, data: unknown) {
    this.name = name;
    this.path = path;
    this.data = data;
  }

  static defaultForName(name: string): Criterion {
    switch (name) {
      case "claim":
        return new Criterion(name, "", "");
      case "device":
      case "domain":
      case "email":
      case "http_method":
      case "http_path":
      case "mcp_tool":
      case "user":
        return new Criterion(name, null, { is: "" });
      case "groups":
        return new Criterion(name, null, { has: "" });
      case "date":
        return new Criterion(name, null, { before: "" });
      case "time_of_day":
        return new Criterion(name, null, {
          before: "",
          timezone: `${moment.tz.guess()}`,
        });
      case "client_certificate":
        return new Criterion(name, null, { fingerprint: [""] });
      case "ssh":
        return new Criterion("ssh_username", null, { is: "" });
      default:
        return new Criterion(name, null, "");
    }
  }

  static fromValue(value: unknown): Criterion | null {
    if (Array.isArray(value)) {
      return null;
    }

    if (typeof value !== "object" || value === null || value === undefined) {
      return null;
    }

    let criterion: Criterion | null = null;
    Object.entries(value).forEach(([k, v]) => {
      const idx = k.indexOf("/");
      if (idx < 0) {
        criterion = new Criterion(k, null, v);
      } else {
        criterion = new Criterion(k.substring(0, idx), k.substring(idx + 1), v);
      }
    });
    return criterion;
  }

  toValue(): any {
    return {
      [isNil(this.path) ? this.name : `${this.name}/${this.path}`]: this.data,
    };
  }
}

export const ALL_LOGICAL_OPERATORS = ["and", "or", "not", "nor"] as const;
type LogicalOperatorTuple = typeof ALL_LOGICAL_OPERATORS;
export type LogicalOperator = LogicalOperatorTuple[number];

const ALL_ACTIONS = ["allow", "deny"] as const;
type ActionTuple = typeof ALL_ACTIONS;
export type Action = ActionTuple[number];

export function isEmptyRule(rule: PplRule): boolean {
  return !(
    rule.allow?.and?.length ||
    rule.allow?.nor?.length ||
    rule.allow?.not?.length ||
    rule.allow?.or?.length ||
    rule.deny?.and?.length ||
    rule.deny?.nor?.length ||
    rule.deny?.not?.length ||
    rule.deny?.or?.length
  );
}

export function isCriterion(criteria: PplCriteria, name: string): boolean {
  const keys = Object.keys(criteria);
  if (keys.length !== 1) {
    return false;
  }

  return keys[0] == name;
}

export function hasToggleCriterion(
  rule: PplRule,
  criterionName: string,
): boolean {
  const ands = rule?.allow?.and ?? [];
  if (ands.length > 0 && ands.every((c) => isCriterion(c, criterionName))) {
    return true;
  }
  const ors = rule?.allow?.or ?? [];
  if (ors.length > 0 && ors.some((c) => isCriterion(c, criterionName))) {
    return true;
  }
  return false;
}

export function addToggleCriterionToRules(
  rules: PplRule[],
  criterionName: string,
): PplRule[] {
  rules = cloneDeep(rules);
  rules.push({
    allow: {
      and: [{ [criterionName]: true }],
    },
  });
  return rules;
}

export function removeToggleCriterionFromRules(
  rules: PplRule[],
  criterionName: string,
): PplRule[] {
  rules = cloneDeep(rules);

  for (let i = rules.length - 1; i >= 0; i--) {
    rules[i] = removeToggleCriterionFromRule(rules[i], criterionName);
    if (isEmptyRule(rules[i])) {
      rules.splice(i, 1);
    }
  }

  return rules;
}

export function removeToggleCriterionFromRule(
  rule: PplRule,
  criterionName: string,
): PplRule {
  rule = cloneDeep(rule);

  if (rule?.allow?.and) {
    if (
      rule.allow.and.length === 1 &&
      isCriterion(rule.allow.and[0], criterionName)
    ) {
      rule.allow.and.splice(0, 1);
    }
  }

  if (rule?.allow?.or) {
    for (let i = rule.allow.or.length - 1; i >= 0; i--) {
      if (isCriterion(rule.allow.or[i], criterionName)) {
        rule.allow.or.splice(i, 1);
      }
    }
  }

  return rule;
}
